import type { TranslationValues } from 'next-intl';
import { FeatureTag } from '@/app/components/shared/FeatureTag';
import { SITE } from '@/app/constants';
import type { HomeQuickEntry } from './types';

const HOME_QUICK_ENTRY_IMAGE_BASE_URL =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset'
    : 'https://image01.cf.vidu.studio/vidu/media-asset';

const HOME_OFFLINE_AVATAR_VIDEO_FILE = '20260826-141120-e88650a7.mp4';
const HOME_OFFLINE_AVATAR_POSTER_FILE = '20260826-141120-a53dad0f.webp';
const HOME_OFFLINE_AVATAR_LOGO_FILES = {
  en: 'en-3c8772dc.webp',
  zh: 'zh-b6c40b70.webp',
} as const;
const getHomeMediaAssetUrl = (fileName: string) => `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/${fileName}`;

export const HOME_PROMO_VIDEO_SRC = getHomeMediaAssetUrl(HOME_OFFLINE_AVATAR_VIDEO_FILE);
export const HOME_PROMO_VIDEO_POSTER = getHomeMediaAssetUrl(HOME_OFFLINE_AVATAR_POSTER_FILE);
export const EAGER_QUICK_ENTRY_IMAGE_COUNT = 3;
export const TOP_MODULE_STEP_SECONDS = 5;

type HomeTranslator = (key: string, values?: TranslationValues) => string;

export const getHomeQuickEntries = (t: HomeTranslator, locale: string): HomeQuickEntry[] => {
  const isChinaSite = SITE === 'china';

  const offlineAvatar: HomeQuickEntry = {
    key: 'offlineAvatar',
    title: t('sidebar.toolsItems.offlineAvatar'),
    // 离线数字人独立入口暂时下线，恢复后改回 /vidu-stream/avatar/offline。
    path: '/create/ai-avatar-generator',
    image: HOME_PROMO_VIDEO_POSTER,
    logo: getHomeMediaAssetUrl(HOME_OFFLINE_AVATAR_LOGO_FILES[locale === 'zh' ? 'zh' : 'en']),
    videoPoster: HOME_PROMO_VIDEO_POSTER,
    badge: <FeatureTag />,
  };

  const reference2video: HomeQuickEntry = {
    key: 'reference2video',
    title: t('sidebar.toolsItems.referenceToVideo'),
    path: '/create/character2video',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/c2v-68811cfd.webp`,
  };
  const img2video: HomeQuickEntry = {
    key: 'img2video',
    title: t('sidebar.toolsItems.imageToVideo'),
    path: '/create/img2video',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/i2v-6e715ae0.webp`,
  };
  const motionControl: HomeQuickEntry = {
    key: 'motionControl',
    title: t('tools.motionControl.title'),
    description: t('tools.motionControl.description'),
    path: '/create/motion-control',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/motion_control-e5c1cbf2.webp`,
  };
  const videoEdit: HomeQuickEntry = {
    key: 'videoEdit',
    title: t('task.action.videoEdit'),
    description: t('task.action.videoQuickEditDescription'),
    path: '/create/video-edit',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/videoedit-de4fb509.webp`,
  };
  const extendVideo: HomeQuickEntry = {
    key: 'extendVideo',
    title: t('task.action.extend'),
    description: t('create.extendForm.description'),
    path: '/create/extension',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/extend-44326210.webp`,
  };
  const lipSync: HomeQuickEntry = {
    key: 'lipSync',
    title: t('tools.lipSync.title'),
    description: t('tools.lipSync.description'),
    path: '/create/lip-sync',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/lipsync-4eaf1b10.webp`,
  };
  const upscaleVideo: HomeQuickEntry = {
    key: 'upscaleVideo',
    title: t('tools.upscale.video.title'),
    description: t('tools.upscale.video.description'),
    path: '/create/upscale-video',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/videoupscale-190d7537.webp`,
  };
  const storyGrid: HomeQuickEntry = {
    key: 'storyGrid',
    title: t('tools.storyGrid.title'),
    description: t('tools.storyGrid.description'),
    path: '/create/story-grid',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/storyboard-18f8ed1d.webp`,
  };
  const reference2image: HomeQuickEntry = {
    key: 'reference2image',
    title: t('sidebar.toolsItems.referenceToImage'),
    path: '/create/reference2image',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/reference2image-366a5726.webp`,
  };
  const text2image: HomeQuickEntry = {
    key: 'text2image',
    title: t('sidebar.toolsItems.textToImage'),
    path: '/create/text2image',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/text2image-4859ca78.webp`,
  };
  const characterCreation: HomeQuickEntry = {
    key: 'characterCreation',
    title: t('tools.characterCreation.title'),
    description: t('tools.characterCreation.description'),
    path: '/create/character-generator',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/transform-2213a1ed.webp`,
  };
  const imageEdit: HomeQuickEntry = {
    key: 'imageEdit',
    title: t('task.action.imageEdit'),
    description: t('task.action.quickEditDescription'),
    path: '/create/image-edit',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/imageedit-8dbd7e4a.webp`,
  };
  const upscaleImage: HomeQuickEntry = {
    key: 'upscaleImage',
    title: t('tools.upscale.image.title'),
    description: t('tools.upscale.image.description'),
    path: '/create/upscale-image',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/imageupscale-285834d9.webp`,
  };
  const templates: HomeQuickEntry = {
    key: 'templates',
    title: t('sidebar.labItems.templates'),
    path: '/templates',
    image: `${HOME_QUICK_ENTRY_IMAGE_BASE_URL}/templates-bab5f63b.webp`,
  };

  if (isChinaSite) {
    // 国内：
    // 第一行：参考生视频、图生视频、动作控制、视频编辑、视频延长、视频对口型、视频增强
    // 第二行：参考生图、文生图、三视图主体、图片编辑、图片增强、AI模板
    return [
      offlineAvatar,
      reference2video,
      img2video,
      motionControl,
      videoEdit,
      extendVideo,
      lipSync,
      upscaleVideo,
      reference2image,
      text2image,
      characterCreation,
      imageEdit,
      upscaleImage,
      templates,
    ];
  }

  // 海外：
  // 第一行：参考生视频、图生视频、多宫格创作、动作控制、视频编辑、视频延长、视频对口型
  // 第二行：参考生图、文生图、AI模板、图片编辑、图片增强、三视图主体、视频增强
  return [
    offlineAvatar,
    reference2video,
    img2video,
    storyGrid,
    motionControl,
    videoEdit,
    extendVideo,
    lipSync,
    reference2image,
    text2image,
    templates,
    imageEdit,
    upscaleImage,
    characterCreation,
    upscaleVideo,
  ];
};

export const HOME_HERO_BG_SRC_CREATE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/create_bg-097f40c1.mp4'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/create_bg-097f40c1.mp4';
export const HOME_HERO_BG_SRC_AGENT =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/s1-4f53414a.mp4'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/s1-4f53414a.mp4';
export const HOME_HERO_POSTER_SRC_CREATE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/create_bg-3e5f65de.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/create_bg-3e5f65de.webp';
export const HOME_HERO_POSTER_SRC_AGENT =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/s1-3b49efa0.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/s1-3b49efa0.webp';
